import React, { useState, useEffect } from 'react';
import { List, Input, Button, message, Popconfirm, Space, Empty, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { getPhraseList, addPhrase, updatePhrase, deletePhrase } from '../../api/phrase';
import './index.css';

const PhraseSettings = () => {
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [saving, setSaving] = useState(false);

  // 获取常用语列表
  const fetchPhrases = async () => {
    try {
      setLoading(true);
      const result = await getPhraseList();
      if (result.code === 200 && result.data) {
        // 按 sortOrder 排序
        const sortedData = (result.data || []).sort((a, b) => a.sortOrder - b.sortOrder);
        setPhrases(sortedData);
      } else {
        message.error(result.msg || '获取常用语列表失败');
      }
    } catch (error) {
      console.error('获取常用语列表失败:', error);
      message.error('获取常用语列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhrases();
  }, []);

  // 开始编辑
  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditingContent(item.content);
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingContent('');
  };

  // 保存编辑
  const handleSaveEdit = async (item) => {
    if (!editingContent.trim()) {
      message.warning('常用语内容不能为空');
      return;
    }

    try {
      setSaving(true);
      const result = await updatePhrase({
        id: item.id,
        content: editingContent.trim(),
        sortOrder: item.sortOrder
      });

      if (result.code === 200) {
        message.success('保存成功');
        setEditingId(null);
        setEditingContent('');
        fetchPhrases();
      } else {
        message.error(result.msg || '保存失败');
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 删除常用语
  const handleDelete = async (id) => {
    try {
      const result = await deletePhrase(id);
      if (result.code === 200) {
        message.success('删除成功');
        fetchPhrases();
      } else {
        message.error(result.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 开始新增
  const handleStartAdd = () => {
    setIsAdding(true);
    setNewContent('');
  };

  // 取消新增
  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewContent('');
  };

  // 保存新增
  const handleSaveAdd = async () => {
    if (!newContent.trim()) {
      message.warning('常用语内容不能为空');
      return;
    }

    try {
      setSaving(true);
      // 计算新的 sortOrder（在已有的最大值上加1）
      const maxSortOrder = phrases.length > 0 
        ? Math.max(...phrases.map(p => p.sortOrder || 0))
        : 0;
      
      const result = await addPhrase({
        content: newContent.trim(),
        sortOrder: maxSortOrder + 1
      });

      if (result.code === 200) {
        message.success('添加成功');
        setIsAdding(false);
        setNewContent('');
        fetchPhrases();
      } else {
        message.error(result.msg || '添加失败');
      }
    } catch (error) {
      console.error('添加失败:', error);
      message.error('添加失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="phrase-settings">
      <Card title="常用语设置" bordered={false}>
        <List
          loading={loading}
          dataSource={phrases}
          locale={{
            emptyText: <Empty description="暂无常用语，点击下方添加" />
          }}
          renderItem={(item) => (
            <List.Item
              key={item.id}
              className="phrase-item"
              actions={
                editingId === item.id
                  ? [
                      <Button
                        key="save"
                        type="primary"
                        size="small"
                        icon={<SaveOutlined />}
                        onClick={() => handleSaveEdit(item)}
                        loading={saving}
                      >
                        保存
                      </Button>,
                      <Button
                        key="cancel"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={handleCancelEdit}
                      >
                        取消
                      </Button>
                    ]
                  : [
                      <Button
                        key="edit"
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleStartEdit(item)}
                      >
                        编辑
                      </Button>,
                      <Popconfirm
                        key="delete"
                        title="确定要删除这条常用语吗？"
                        onConfirm={() => handleDelete(item.id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button
                          type="link"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                        >
                          删除
                        </Button>
                      </Popconfirm>
                    ]
              }
            >
              {editingId === item.id ? (
                <Input.TextArea
                  value={editingContent}
                  onChange={(e) => setEditingContent(e.target.value)}
                  placeholder="请输入常用语内容"
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  style={{ flex: 1 }}
                />
              ) : (
                <div className="phrase-content" onClick={() => handleStartEdit(item)}>
                  {item.content}
                </div>
              )}
            </List.Item>
          )}
        />

        {/* 新增常用语区域 */}
        {isAdding ? (
          <div className="add-phrase-area">
            <Input.TextArea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder="请输入常用语内容"
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ flex: 1 }}
            />
            <Space style={{ marginTop: 8 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveAdd}
                loading={saving}
              >
                保存
              </Button>
              <Button icon={<CloseOutlined />} onClick={handleCancelAdd}>
                取消
              </Button>
            </Space>
          </div>
        ) : (
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={handleStartAdd}
            style={{ marginTop: 16 }}
          >
            添加常用语
          </Button>
        )}
      </Card>
    </div>
  );
};

export default PhraseSettings;
